/**
 * Error severity levels for categorization and handling
 */
export enum ErrorSeverity {
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
  CRITICAL = "critical",
}

/**
 * Error categories for better error tracking and handling
 */
export enum ErrorCategory {
  NETWORK = "network",
  VALIDATION = "validation",
  AUTHENTICATION = "authentication",
  AUTHORIZATION = "authorization",
  NOT_FOUND = "not_found",
  SERVER = "server",
  CLIENT = "client",
  UNKNOWN = "unknown",
}

/**
 * Base application error with enhanced metadata
 */
export class AppError extends Error {
  public readonly timestamp: Date;
  public readonly category: ErrorCategory;
  public readonly severity: ErrorSeverity;
  public readonly code?: string;
  public readonly details?: Record<string, unknown>;
  public readonly originalError?: Error;

  constructor(
    message: string,
    category: ErrorCategory = ErrorCategory.UNKNOWN,
    severity: ErrorSeverity = ErrorSeverity.MEDIUM,
    options?: {
      code?: string;
      details?: Record<string, unknown>;
      originalError?: Error;
    },
  ) {
    super(message);
    this.name = "AppError";
    this.timestamp = new Date();
    this.category = category;
    this.severity = severity;
    this.code = options?.code;
    this.details = options?.details;
    this.originalError = options?.originalError;

    // Maintains proper stack trace for where our error was thrown
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AppError);
    }
  }

  /**
   * Serialize error for logging or transmission
   */
  toJSON(): Record<string, unknown> {
    return {
      name: this.name,
      message: this.message,
      category: this.category,
      severity: this.severity,
      code: this.code,
      timestamp: this.timestamp.toISOString(),
      details: this.details,
      stack: this.stack,
    };
  }
}

/**
 * Network-related errors (API calls, connectivity)
 */
export class NetworkError extends AppError {
  constructor(
    message: string,
    options?: {
      code?: string;
      details?: Record<string, unknown>;
      originalError?: Error;
    },
  ) {
    super(message, ErrorCategory.NETWORK, ErrorSeverity.HIGH, options);
    this.name = "NetworkError";
  }
}

/**
 * Validation errors (form validation, data validation)
 */
export class ValidationError extends AppError {
  constructor(
    message: string,
    options?: {
      code?: string;
      details?: Record<string, unknown>;
      originalError?: Error;
    },
  ) {
    super(message, ErrorCategory.VALIDATION, ErrorSeverity.LOW, options);
    this.name = "ValidationError";
  }
}

/**
 * Authentication errors (login, token expiry)
 */
export class AuthenticationError extends AppError {
  constructor(
    message: string,
    options?: {
      code?: string;
      details?: Record<string, unknown>;
      originalError?: Error;
    },
  ) {
    super(
      message,
      ErrorCategory.AUTHENTICATION,
      ErrorSeverity.CRITICAL,
      options,
    );
    this.name = "AuthenticationError";
  }
}

/**
 * Authorization errors (permission denied)
 */
export class AuthorizationError extends AppError {
  constructor(
    message: string,
    options?: {
      code?: string;
      details?: Record<string, unknown>;
      originalError?: Error;
    },
  ) {
    super(message, ErrorCategory.AUTHORIZATION, ErrorSeverity.HIGH, options);
    this.name = "AuthorizationError";
  }
}
